// The async Clipboard API only exists in secure contexts (HTTPS or
// localhost) — on a plain-HTTP origin `navigator.clipboard` is undefined
// entirely, so every copy button would silently fail. This falls back to
// the older execCommand('copy') approach (a hidden, selected textarea),
// which works regardless of context.
const copyTextToClipboardFallback = text => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  document.body.appendChild(textarea);
  textarea.select();
  textarea.setSelectionRange(0, text.length);

  let succeeded = false;
  try {
    succeeded = document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }

  if (!succeeded) {
    throw new Error('execCommand copy was unsuccessful');
  }
};

/**
 * Writes a text string to the system clipboard.
 *
 * @async
 * @param {string} data text to be written to the clipboard
 * @throws {Error} unable to copy text to clipboard
 */
export const copyTextToClipboard = async data => {
  const text =
    typeof data === 'object' && data !== null
      ? JSON.stringify(data, null, 2)
      : String(data ?? '');

  try {
    if (!navigator.clipboard) throw new Error('Clipboard API unavailable');
    await navigator.clipboard.writeText(text);
  } catch (error) {
    try {
      copyTextToClipboardFallback(text);
    } catch (fallbackError) {
      throw new Error(
        `Unable to copy text to clipboard: ${fallbackError.message}`
      );
    }
  }
};

/**
 * Handles OTP paste events by extracting numeric digits from clipboard data.
 *
 * @param {ClipboardEvent} event - The paste event from the clipboard
 * @param {number} maxLength - Maximum number of digits to extract (default: 6)
 * @returns {string|null} - Extracted numeric string or null if invalid
 */
export const handleOtpPaste = (event, maxLength = 6) => {
  if (!event?.clipboardData) return null;

  const pastedData = event.clipboardData
    .getData('text')
    .replace(/\D/g, '') // Remove all non-digit characters
    .slice(0, maxLength); // Limit to maxLength digits

  // Only return if we have the exact expected length
  return pastedData.length === maxLength ? pastedData : null;
};
